// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { object, array, bool } from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { SkeletonPlaceholder } from '@carbon/react';

import { API_PROFORM } from 'Constants';
import SuccessCheckoutComponent from 'Components/Checkout/SuccessCheckout';
import OrderProducts from 'Containers/Order/OrderProducts';
import OrderSummary from 'Containers/Order/OrderSummary';
import OrderInfo from 'Containers/Order/OrderInfo';
import usePermissions from 'Hooks/usePermissions';
import useToastNotifications from 'Hooks/useToastNotifications';
import download from 'Utils/download';
import useOrderStatus from 'Hooks/useOrderStatus';

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 6rem;
  width: auto;
  margin: 10px 0 30px;
`;

const SuccessCheckout = memo(({
  products,
  order,
  isFirstOrder,
  isLastOrder,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [errors, setErrors] = useState([]);
  const { isExternalReseller } = usePermissions();
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const orderStatus = useOrderStatus(order);

  const stockReservation = order?.statusId === 2;

  useEffect(() => {
    if (order?.warnings?.length) {
      setErrors([{ message: order?.warnings.map(i => i?.message).join('\r\n'), type: 'error' }]);
    } else if (orderStatus?.statusId === 0) {
      setErrors([{ message: t('orders.processing'), type: 'wait' }]);
    } else if (orderStatus?.statusId === 3 && orderStatus?.status === 'CREDIT_LIMIT' && orderStatus?.supplierId !== null) {
      setErrors([{ message: t('order.creditLimitSupplier'), type: 'warning' }]);
    } else if (orderStatus?.statusId === 3 && orderStatus?.status === 'WAS_BLOCKED_OUTSTANDING' && orderStatus?.supplierId !== null) {
      setErrors([{ message: t('orders.outstandingBalanceSupplier'), type: 'warning' }]);
    } else if (orderStatus?.statusId === 3 && orderStatus?.status === 'CREDIT_LIMIT') {
      setErrors([{ message: t('orders.creditLimit'), type: 'warning' }]);
    } else if (orderStatus?.statusId === 3 && orderStatus?.status === 'WAS_BLOCKED_OUTSTANDING') {
      setErrors([{ message: t('orders.outstandingBalance'), type: 'warning' }]);
    } else if (order?.statusId !== undefined) {
      setErrors([{ message: t('order.success', { orderId: order?.orderNumber || order?.orderId }), type: 'success' }]);
    }
  }, [
    order?.warnings,
    orderStatus?.statusId,
    orderStatus?.status,
    orderStatus?.supplierId,
  ]);

  const handleOnGenerateInvoice = async () => {
    setDownloading(true);
    try {
      await download(`${ API_PROFORM }/${order.id}`);
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: 'Could not generate proforma'
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SuccessCheckoutComponent
      order={ order || {} }
      loading={ downloading }
      onGenerateInvoice={ handleOnGenerateInvoice }
      errors={ errors }
      isLastOrder={ isLastOrder }
      isFirstOrder={ isFirstOrder }
    >
      { products?.length > 0 ? (
        <>
          <OrderProducts
            order={ order || {} }
            editable={ false }
            products={ products || [] }
          />
          <div className="summaryContainer">
            <OrderSummary
              order={ order || {} }
              orderType={ stockReservation ? 'reservation' : 'ds' }
              totals={ order?.getTotals() }
              canReserve={ false }
              canShowButtons={ false }
              canPlaceOrder={ false }
              displayVat={ !isExternalReseller }
            />
          </div>
        </>
      ) : <StyledSkeletonPlaceholder /> }
      { isLastOrder && (
        <OrderInfo order={ order || {} } editable={ false } />
      )}
    </SuccessCheckoutComponent>
  );
});
SuccessCheckout.propTypes = {
  order: object,
  products: array,
  isFirstOrder: bool,
  isLastOrder: bool,
};
SuccessCheckout.displayName = 'SuccessCheckout';

export default SuccessCheckout;
